import { z } from "zod";
import { AlgorithmId, StageId } from "@algoquest/schemas";

/**
 * `assignments` domain DTOs (Master Spec §33/§34, Phase 12). An
 * assignment is a teacher-picked set of already-registered official
 * levels (see `CreateAssignmentRequestSchema`'s doc comment for why
 * "already-registered" is a real constraint, not an oversight) attached
 * to one class, with an optional due date.
 */
export const CreateAssignmentRequestSchema = z.object({
  classId: z.string().uuid(),
  title: z.string().trim().min(1).max(120),
  dueAt: z.string().datetime().nullable().default(null),
  /**
   * Every number here must already have a `game_levels` row — i.e. some
   * player has reached it at least once, since Phase 10's lazy-bootstrap
   * design (`POST /api/v1/levels`) is still the only thing that creates
   * them; Phase 15's full 100-level authoring pass hasn't run yet. A
   * teacher assigning a level nobody has ever played will get a
   * validation error naming which numbers aren't registered yet — see
   * `assignments.service.ts`'s `createAssignment`.
   */
  globalLevelNumbers: z.array(z.number().int().min(1).max(100)).min(1),
});
export type CreateAssignmentRequest = z.infer<typeof CreateAssignmentRequestSchema>;

export const AssignmentLevelDTOSchema = z.object({
  globalLevelNumber: z.number().int().min(1).max(100),
  stage: StageId,
  algorithm: AlgorithmId,
});
export type AssignmentLevelDTO = z.infer<typeof AssignmentLevelDTOSchema>;

/**
 * The STUDENT-facing shape (`GET /api/v1/assignments`): `submitted` is
 * always freshly derived from the caller's own `level_progress` at read
 * time, never trusted from the stored `assignment_submissions` row
 * alone (see that table's own doc comment in `@algoquest/database`);
 * `submittedAt` is the stored first-completion timestamp when one was
 * captured, `null` if the assignment isn't complete OR was completed
 * before this timestamp-capturing hook existed.
 */
export const AssignmentDTOSchema = z.object({
  id: z.string().uuid(),
  classId: z.string().uuid(),
  className: z.string(),
  title: z.string(),
  dueAt: z.string().datetime().nullable(),
  levels: z.array(AssignmentLevelDTOSchema),
  submitted: z.boolean(),
  submittedAt: z.string().datetime().nullable(),
});
export type AssignmentDTO = z.infer<typeof AssignmentDTOSchema>;

/**
 * The TEACHER-facing shape (`GET /api/v1/assignments/teaching`): a
 * teacher cares about the class's aggregate progress, not their own
 * completion (they have none) — `submittedCount`/`totalMembers`
 * replaces `submitted`/`submittedAt` for exactly that reason, rather
 * than overloading one DTO with fields that are meaningless depending
 * on who's asking.
 */
export const TeachingAssignmentDTOSchema = z.object({
  id: z.string().uuid(),
  classId: z.string().uuid(),
  className: z.string(),
  title: z.string(),
  dueAt: z.string().datetime().nullable(),
  levels: z.array(AssignmentLevelDTOSchema),
  submittedCount: z.number().int().nonnegative(),
  totalMembers: z.number().int().nonnegative(),
});
export type TeachingAssignmentDTO = z.infer<typeof TeachingAssignmentDTOSchema>;

export const CreateAssignmentResponseSchema = z.object({
  assignment: AssignmentDTOSchema,
});
export type CreateAssignmentResponse = z.infer<typeof CreateAssignmentResponseSchema>;

export const ListAssignmentsResponseSchema = z.object({
  assignments: z.array(AssignmentDTOSchema),
});
export type ListAssignmentsResponse = z.infer<typeof ListAssignmentsResponseSchema>;

export const ListTeachingAssignmentsResponseSchema = z.object({
  assignments: z.array(TeachingAssignmentDTOSchema),
});
export type ListTeachingAssignmentsResponse = z.infer<typeof ListTeachingAssignmentsResponseSchema>;
